/**
 * QueueNav — back link to the round Tablero plus, when the browser handed us a
 * queue snapshot, "N de M" position and prev/next arrows over that FROZEN page
 * of expedientes (see queueState.ts for why it never refetches).
 *
 * Cold deep-link (no/invalid snapshot): only the back link renders — a counter
 * reconstructed by refetching would lie the moment anything is adjudicated.
 *
 * Keyboard: `[` / `]` for prev/next, ignored while typing in the justification
 * field (or any input/textarea/contenteditable).
 */

import { useCallback, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { tableroPath, type ReviewQueueState } from "./queueState";

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

export function QueueNav({
  roundId,
  queue,
}: {
  roundId: string;
  queue: ReviewQueueState | null;
}) {
  const navigate = useNavigate();

  const go = useCallback(
    (delta: -1 | 1) => {
      if (!queue) return;
      const nextIndex = queue.index + delta;
      if (nextIndex < 0 || nextIndex >= queue.ids.length) return;
      navigate(`/rounds/${roundId}/review/${queue.ids[nextIndex]}`, {
        state: { queue: { ...queue, index: nextIndex } },
      });
    },
    [navigate, queue, roundId],
  );

  useEffect(() => {
    if (!queue) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (event.key === "[") go(-1);
      else if (event.key === "]") go(1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [go, queue]);

  const position = queue ? (queue.page - 1) * queue.pageSize + queue.index + 1 : 0;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}
      useFlexGap
    >
      <Link
        component={RouterLink}
        to={tableroPath(roundId, queue)}
        underline="hover"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        Tablero de la ronda
      </Link>

      {queue && (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          <Tooltip title="Expediente anterior ( [ )">
            <span>
              <IconButton
                size="small"
                aria-label="Expediente anterior"
                onClick={() => go(-1)}
                disabled={queue.index === 0}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums", px: 0.5 }}
          >
            {position} de {queue.total}
          </Typography>
          <Tooltip title="Expediente siguiente ( ] )">
            <span>
              <IconButton
                size="small"
                aria-label="Expediente siguiente"
                onClick={() => go(1)}
                disabled={queue.index >= queue.ids.length - 1}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
}
